// src/screens/MyTrainers.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Title from "../components/Title";
import CreditsBalance from "../components/CreditBalance";
import Navbar from "../components/Navbar";
import TrainerScheduleCard from "../components/TrainerScheduleCard";
import { getAllTrainers, getUserProfile, getUserSessions } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import trainerImg from "../assets/trainer.png";

/* --------------------------------- types ---------------------------------- */
type MyTrainer = {
  id: string;
  name: string;
  rate: number;
  age: number;
  sex: "M" | "F" | "Other";
  schedule: { day: string; time: string }[];
  imageUrl?: string;
  rawTrainer?: any;
};

/* ------------------------------ components -------------------------------- */

const TrainerCard: React.FC<{ trainer: MyTrainer; onClick: () => void }> = ({
  trainer,
  onClick,
}) => {
  return (
    <div className="mb-4">
      <TrainerScheduleCard
        name={trainer.name}
        rate={trainer.rate}
        gradient="dark"
        meta={`${trainer.age}${trainer.sex}`}
        imageSrc={String(trainer.imageUrl || trainerImg)}
        imgHeight={140}
        bandHeight={100}
        schedule={trainer.schedule}
        onClick={onClick}
      />
    </div>
  );
};

const emptyStateMessages = [
  "Please book a session with a trainer to begin!",
  "Please book a session with a trainer to begin! Your first session is waiting.",
  "Please book a session with a trainer to begin! We’ll keep your favorites here.",
  "Please book a session with a trainer to begin! Start training when you’re ready."
];

/* --------------------------------- screen --------------------------------- */
export default function MyTrainers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [userCredits, setUserCredits] = useState(0);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [myTrainers, setMyTrainers] = useState<MyTrainer[]>([]);
  const [loading, setLoading] = useState(true);
  const [emptyMessage, setEmptyMessage] = useState(emptyStateMessages[0]);

  // Fetch user credits
  useEffect(() => {
    const fetchUserCredits = async () => {
      if (!user?.id) return;

      try {
        setCreditsLoading(true);
        const profileResponse = await getUserProfile(user.id);
        console.log('MyTrainers - Profile response:', profileResponse);
        setUserCredits(profileResponse.user?.credits || 0);
      } catch (error) {
        console.error('MyTrainers - Error fetching user credits:', error);
      } finally {
        setCreditsLoading(false);
      }
    };

    fetchUserCredits();
  }, [user?.id]);

  useEffect(() => {
    const fetchMyTrainers = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setEmptyMessage(emptyStateMessages[Math.floor(Math.random() * emptyStateMessages.length)]);
        const [sessionsResponse, trainersResponse] = await Promise.all([
          getUserSessions(user.id),
          getAllTrainers()
        ]);

        const trainerIds = new Set<string>();
        (sessionsResponse?.sessions || []).forEach((session: any) => {
          if (session?.trainerId) {
            trainerIds.add(session.trainerId);
          }
        });

        const trainers = (trainersResponse?.trainers || []).filter((t: any) =>
          trainerIds.has(t._id)
        );

        const upcomingByTrainer = new Map<string, { day: string; time: string }[]>();
        (sessionsResponse?.upcomingSessions || []).forEach((session: any) => {
          if (!session?.trainerId) return;
          const list = upcomingByTrainer.get(session.trainerId) || [];
          list.push({
            day: session.date || "Upcoming",
            time: session.time || "Booked"
          });
          upcomingByTrainer.set(session.trainerId, list);
        });

        const mapped: MyTrainer[] = trainers.map((trainer: any) => {
          const schedule = upcomingByTrainer.get(trainer._id) || [{ day: "Upcoming", time: "Booked" }];
          return {
            id: trainer._id,
            name: trainer.name || "Trainer",
            rate: trainer.creditsPerHour || 1,
            age: Number(trainer.age || 0),
            sex: trainer.sex || "Other",
            schedule: schedule.slice(0, 3),
            imageUrl: trainer.profilePic || trainerImg,
            rawTrainer: trainer
          };
        });

        setMyTrainers(mapped);
      } catch (error) {
        console.error("MyTrainers - Error fetching trainers:", error);
        setMyTrainers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMyTrainers();
  }, [user?.id]);

  const handleTrainerClick = (trainer: MyTrainer) => {
    if (trainer.rawTrainer) {
      navigate("/student/trainer-details", { state: { trainer: trainer.rawTrainer } });
      return;
    }
    navigate("/student/trainer-details", { state: { trainer } });
  };

  return (
    <div className="h-[100dvh] bg-[#F7F7F7] overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
      <div className="mx-auto w-full pt-[max(12px,env(safe-area-inset-top))]">
        <Navbar
          onBack={() => navigate(-1)}
          background="transparent"
          right={
            <CreditsBalance
              balance={creditsLoading ? 0 : userCredits}
              variant="white-with-button"
              onAdd={() => navigate("/student/buy-credits")}
            />
          }
        />

        {/* Content */}
        <main className="px-5">
          {/* Section Title */}
          <Title text="// MY TRAINERS" />

          {/* Trainer List */}
          <div className="mt-4 pb-20">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading trainers...</div>
            ) : myTrainers.length === 0 ? (
              <div className="text-center py-10 px-6 text-gray-500">
                {emptyMessage}
              </div>
            ) : (
              myTrainers.map((trainer) => (
                <TrainerCard
                  key={trainer.id}
                  trainer={trainer}
                  onClick={() => handleTrainerClick(trainer)}
                />
              ))
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
